import {
    AfterViewInit,
    Directive,
    ElementRef,
    EnvironmentInjector,
    ViewContainerRef,
    computed,
    effect,
    inject,
    signal,
} from "@angular/core";

import { CerrixErrorLabelComponent } from "@app/shared/atoms/cerrix-error-label/cerrix-error-label.component";
import { CerrixInputValidationRegistryService } from "./cerrix-input-validation-registry.service";
import { CerrixInputValidationRequiredDirective } from "./cerrix-input-validation-required.directive";
import {
    CERRIX_INPUT_VALUE_SOURCE,
    CerrixInputValueSource,
} from "./models/cerrix-input-value-source.interface";
import { CerrixInputValidationError } from "./models/cerrix-input-validation-error.interface";
import { CerrixInputValidation } from "./models/cerrix-input-validation.interface";

@Directive({
    selector: "[validationErrors]",
    host: {
        "[class.error]": "hasErrors()",
    },
})
export class CerrixInputValidationErrorsDirective implements AfterViewInit {
    private errorMap = signal(new Map<string, CerrixInputValidationError>());

    public errors = computed(() =>
        [...this.errorMap().values()].sort((a, b) => a.priority - b.priority),
    );

    public hasErrors = computed(() => this.errors().length > 0);

    private viewContainer = inject(ViewContainerRef);
    private injector = inject(EnvironmentInjector);
    private registry = inject(CerrixInputValidationRegistryService);
    private el = inject(ElementRef<HTMLElement>);

    // The required directive marks either this element or the field component this element
    // lives in, so it is the one directive that cannot reach this one by itself. The search
    // must be unrestricted: `host: true` would stop at this element's own component and never
    // reach the host of the component whose template it lives in, which is where a page puts
    // the directive. The first field that asks claims it, so one directive serves one field.
    private required = inject(CerrixInputValidationRequiredDirective, { optional: true });
    private valueSource: CerrixInputValueSource | null = inject(CERRIX_INPUT_VALUE_SOURCE, {
        optional: true,
    });

    private errorComponentRef?: ReturnType<ViewContainerRef["createComponent"]>;
    private textNode?: Text;

    private fieldId!: string;

    ngAfterViewInit() {
        this.fieldId = this.resolveFieldId();

        effect(
            () => {
                if (this.hasErrors()) {
                    this.ensureComponent();
                    this.textNode!.nodeValue = this.errors()[0]?.message ?? "";
                } else {
                    this.destroyComponent();
                }
            },
            { injector: this.injector },
        );

        this.required?.attachTo(this, this.valueSource);
    }

    register(cerrixInputValidation: CerrixInputValidation) {
        this.registry.registerInputValidation(this, cerrixInputValidation);
    }

    fieldName() {
        return this.fieldId;
    }

    // A write always stores a fresh Map, which signals compare by identity, so writing an
    // unchanged result still counts as a change. Both of these therefore check first: a
    // validator that re-runs and reaches the same verdict leaves the signal alone.
    add(error: CerrixInputValidationError) {
        const current = this.errorMap().get(error.key);
        if (current && current.message === error.message && current.priority === error.priority) {
            return;
        }

        const map = new Map(this.errorMap());
        map.set(error.key, error);
        this.errorMap.set(map);
    }

    remove(key: string) {
        if (!this.errorMap().has(key)) return;

        const map = new Map(this.errorMap());
        map.delete(key);
        this.errorMap.set(map);
    }

    reset() {
        this.errorMap.set(new Map());
    }

    private ensureComponent() {
        if (this.errorComponentRef) return;

        this.errorComponentRef = this.viewContainer.createComponent(CerrixErrorLabelComponent, {
            environmentInjector: this.injector,
        });

        this.textNode = document.createTextNode("");
        this.errorComponentRef.location.nativeElement.appendChild(this.textNode);
    }

    private destroyComponent() {
        this.textNode?.remove();
        this.textNode = undefined;

        this.errorComponentRef?.destroy();
        this.errorComponentRef = undefined;

        this.viewContainer.clear();
    }

    private resolveFieldId(): string {
        const native = this.el.nativeElement;

        const direct = native.getAttribute("name");
        if (direct) return direct;

        const nested = native.querySelector("input[name], textarea[name]");
        if (nested) return nested.getAttribute("name")!;

        throw new Error("validationErrors requires [name] on element or nested input/textarea.");
    }
}
